/**
 * Memorial physical product requests — Wave 4A customer API client.
 */
import axios from 'axios';
import { getApiBase } from './apiBase';
import { product_size_from_type as sizeFromProductId } from './memorialProductRequestMapping';
import { KEEPSAKE_IMAGE_SOURCES } from './memorialPhotoLinkBuilder';

const API = getApiBase();

export const ORDER_STATUS_LABELS = {
  draft: { label: 'Draft', description: 'Your keepsake request has been saved.' },
  awaiting_memorial_setup: {
    label: 'Awaiting memorial setup',
    description: 'Publish the memorial to activate the link.',
  },
  awaiting_qr_setup: {
    label: 'Awaiting link setup',
    description: 'The memorial link is being prepared.',
  },
  preview_ready: { label: 'Preview ready', description: 'Review your keepsake preview when it is ready.' },
  customer_approved: { label: 'Request saved', description: 'Your keepsake request is saved for review.' },
  in_production: { label: 'In production', description: 'Being prepared.' },
  dispatched: { label: 'Dispatched', description: 'On its way.' },
  completed: { label: 'Completed', description: 'Finished.' },
  cancelled: { label: 'Cancelled', description: 'This request was cancelled.' },
};

export function orderStatusCopy(status) {
  return ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS.draft;
}

export function buildCreatePayload({
  memorial,
  person,
  product,
  frameStyle,
  layout,
  orientation,
  imageSource,
  selectedImageRef,
  imagePosition,
  imageEdits,
  tributeText,
  uploadedKeepsake,
}) {
  const uploadMeta = uploadedKeepsake || {};
  const portraitRef = imageSource === 'portrait' ? selectedImageRef || null : null;
  return {
    memorial_id: memorial?.id,
    person_id: person?.id,
    product_type: product?.productType || product?.id,
    product_label: product?.title || product?.id,
    size: sizeFromProductId(product?.id || product?.productType),
    orientation: orientation || 'portrait',
    frame_colour: frameStyle?.id === 'white' ? 'white' : frameStyle?.id === 'none' ? 'none' : 'black',
    image_source: imageSource,
    selected_image_ref: portraitRef,
    uploaded_image_key: imageSource === 'upload' ? uploadMeta.uploaded_image_key || null : null,
    uploaded_image_url: imageSource === 'upload' ? uploadMeta.uploaded_image_url || null : null,
    original_filename: imageSource === 'upload' ? uploadMeta.original_filename || null : null,
    mime_type: imageSource === 'upload' ? uploadMeta.mime_type || null : null,
    image_width: imageSource === 'upload' ? uploadMeta.image_width ?? null : null,
    image_height: imageSource === 'upload' ? uploadMeta.image_height ?? null : null,
    uploaded_at: imageSource === 'upload' ? uploadMeta.uploaded_at || null : null,
    image_position: imagePosition || {},
    image_edits: imageEdits || {},
    layout_id: layout?.id,
    tribute_text: tributeText || null,
  };
}

/** Resolve image fields for API save — blocks blob until persisted upload completes. */
export function resolveMemorialProductRequestImage(builder, personPortraitUrl) {
  const source = builder?.imageSource;
  if (source === KEEPSAKE_IMAGE_SOURCES.upload) {
    const ks = builder?.uploadedKeepsake;
    if (ks?.uploaded_image_key || ks?.uploaded_image_url) {
      return { ok: true, imageSource: source, selectedImageRef: null, uploadedKeepsake: ks };
    }
    const ref = builder?.customImageUrl || '';
    if (!ref || ref.startsWith('blob:')) {
      return { ok: false, reason: 'upload_pending' };
    }
    return { ok: false, reason: 'upload_pending' };
  }
  if (source === KEEPSAKE_IMAGE_SOURCES.portrait && personPortraitUrl) {
    return { ok: true, imageSource: source, selectedImageRef: personPortraitUrl };
  }
  if (source === KEEPSAKE_IMAGE_SOURCES.later) {
    return { ok: true, imageSource: source, selectedImageRef: personPortraitUrl || null };
  }
  return { ok: false, reason: 'no_image' };
}

export async function createMemorialProductRequest(payload) {
  const { data } = await axios.post(`${API}/memorial-product-requests`, payload);
  return data;
}

export async function listMemorialProductRequests(memorialId) {
  const params = memorialId ? { memorial_id: memorialId } : {};
  const { data } = await axios.get(`${API}/memorial-product-requests`, { params });
  return data;
}

export async function getMemorialProductRequest(requestId) {
  const { data } = await axios.get(`${API}/memorial-product-requests/${requestId}`);
  return data;
}

export function memorialLinkLabel(request, published) {
  if (!published) return 'Activation pending';
  if (request?.live_qr_available && request?.mylegacy_qr_url) return 'Dynamic memorial link ready';
  if (request?.qr_status === 'awaiting_setup') return 'Memorial link being prepared';
  return request?.qr_status === 'tested' ? 'Memorial link tested' : 'Dynamic memorial link';
}
